import { FC, useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { getAllSubscriptions, cancelUserSubscription } from '@/services/subscription.service';
import { getErrorMessage } from '@/utils/error';
import { Search, Filter, RefreshCw, Calendar, CreditCard, AlertCircle } from 'lucide-react';
import { Table, Column } from '@/components/ui/Table';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Pagination } from '@/components/ui';

import { Subscription } from '@/types/payment';

const InternalSubscriptionsTable: FC = () => {
    const { addToast } = useToast();
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);

    // Cancel modal state
    const [cancelModal, setCancelModal] = useState<{ open: boolean; userId?: string; userName?: string }>({ open: false });
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to page 1 on search change
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const result = await getAllSubscriptions({
                search: debouncedSearch || undefined,
                status: status || undefined,
                page,
                limit
            });
            // Handle both new paginated response and potential old array response for backward compat check (though we updated service)
            if (Array.isArray(result)) {
                setSubscriptions(result);
                setTotal(result.length);
            } else {
                setSubscriptions(result.data || []);
                setTotal(result.total || 0);
            }
        } catch (err) {
            console.error('Failed to fetch subscriptions:', err);
            addToast('Failed to load subscriptions.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, [debouncedSearch, status, page]);

    const handleCancelSubscription = async () => {
        if (!cancelModal.userId) return;
        setCancelling(true);
        try {
            await cancelUserSubscription(cancelModal.userId, cancelReason);
            addToast('Subscription cancelled successfully.', 'success');
            setCancelModal({ open: false });
            setCancelReason('');
            fetchSubscriptions(); // Refresh subscriptions after cancellation
        } catch (err) {
            addToast(getErrorMessage(err) || 'Failed to cancel subscription.', 'error');
        } finally {
            setCancelling(false);
        }
    };

    const columns: Column<Subscription>[] = [
        {
            key: 'user',
            header: 'Customer',
            className: 'w-[280px]',
            render: (s) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-slate-600">
                            {s.user?.name ? s.user.name.charAt(0).toUpperCase() : '?'}
                        </span>
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-slate-900 truncate">{s.user?.name || 'Unknown User'}</span>
                        <span className="text-xs text-slate-500 truncate font-mono">{s.user?.email || 'No email'}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'plan',
            header: 'Plan',
            render: (s) => {
                let name = s.plan?.displayName || s.plan?.name || 'Manual Plan';
                if (name.includes('Marketing & Sales Experience Manager')) name = name.replace('Marketing & Sales Experience Manager', 'MXM & SXM');
                else if (name.includes('Marketing Experience Manager')) name = name.replace('Marketing Experience Manager', 'MXM');
                else if (name.includes('Sales Experience Manager')) name = name.replace('Sales Experience Manager', 'SXM');

                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-50 text-xs font-medium text-slate-600 border border-slate-200 whitespace-nowrap">
                        {name}
                    </span>
                );
            }
        },
        {
            key: 'status',
            header: 'Status',
            className: 'w-[140px]',
            render: (s) => {
                const statusStyles = {
                    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
                    trialing: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
                    past_due: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
                    canceled: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
                    incomplete: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
                };
                const style = statusStyles[s.status as keyof typeof statusStyles] || statusStyles.canceled;

                return (
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        <span className="capitalize">{s.status.replace(/_/g, ' ')}</span>
                    </div>
                );
            }
        },
        {
            key: 'period',
            header: 'Current Period',
            render: (s) => {
                const start = new Date(s.currentPeriodStart || '');
                const end = new Date(s.currentPeriodEnd || '');
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return <span className="text-slate-400 text-xs">—</span>;

                return (
                    <div className="flex flex-col whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span>{end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono mt-0.5 pl-4">
                            Started {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'platform',
            header: 'Platform',
            render: (s) => {
                const isManual = s.stripeSubscriptionId?.startsWith('sub_manual_');
                return (
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${isManual ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
                        }`}>
                        <CreditCard className="w-3 h-3" />
                        {isManual ? 'Manual' : 'Stripe'}
                    </div>
                );
            }
        },
        /*
        {
            key: 'actions',
            header: '',
            className: 'text-right min-w-[100px]',
            render: (s) => (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        setCancelModal({ open: true, userId: s.user.id, userName: s.user.name });
                    }}
                    className="text-red-600 hover:bg-red-50 border-red-200"
                >
                    Cancel
                </Button>
            )
        }
        */
    ];

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="w-full md:max-w-md relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <Input
                        placeholder="Search subscribers..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-10 w-full bg-white border-slate-200 focus:border-slate-400 focus:ring-slate-400/10 transition-all font-medium text-slate-700 placeholder:text-slate-400"
                    />
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative group w-full md:w-auto">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none z-10" />
                        <select
                            value={status}
                            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                            className="h-10 pl-9 pr-10 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg focus:ring-2 focus:ring-slate-900/5 focus:border-slate-300 block w-full md:w-48 outline-none appearance-none cursor-pointer hover:border-slate-300 transition-all"
                        >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="trialing">Trialing</option>
                            <option value="past_due">Past Due</option>
                            <option value="canceled">Canceled</option>
                            <option value="incomplete">Incomplete</option>
                            <option value="unpaid">Unpaid</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>

                    <Button variant="secondary" onClick={fetchSubscriptions} className="h-10 w-10 p-0 flex items-center justify-center bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-600" disabled={loading} title="Refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <Spinner size="lg" />
                    <p className="mt-4 text-sm text-slate-500 font-medium tracking-wide">Loading subscriptions...</p>
                </div>
            ) : (
                <div className="space-y-4">
                    <Table
                        columns={columns}
                        data={subscriptions}
                        emptyMessage="No subscriptions found matching your criteria."
                        hoverable={true}
                        variant="bordered"
                        className="bg-white rounded-xl shadow-sm overflow-hidden"
                    />

                    {/* Pagination Controls */}
                    {total > 0 && (
                        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 px-2 mt-4">
                            <span className="text-sm text-slate-500 font-medium">
                                Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total} results
                            </span>
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Cancel Modal */}
            <Modal
                isOpen={cancelModal.open}
                onClose={() => { setCancelModal({ open: false }); setCancelReason(''); }}
                title="Cancel Subscription"
                size="sm"
            >
                <div className="space-y-5">
                    <div className="p-4 bg-red-50 border border-red-100 rounded-lg flex gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                        <div>
                            <p className="text-sm font-bold text-red-900">Terminating Access</p>
                            <p className="text-sm text-red-700 mt-1">
                                User <span className="font-bold">{cancelModal.userName}</span> will lose all premium features immediately.
                            </p>
                        </div>
                    </div>

                    <Textarea
                        label="Cancellation Reason"
                        placeholder="Why is this subscription being cancelled? (Mandatory for audit)"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                        fullWidth
                    />
                </div>

                <ModalFooter>
                    <Button variant="secondary" onClick={() => { setCancelModal({ open: false }); setCancelReason(''); }}>
                        Go Back
                    </Button>
                    <Button
                        variant="primary"
                        className="bg-red-600 hover:bg-red-700 text-white border-transparent"
                        onClick={handleCancelSubscription}
                        isLoading={cancelling}
                        disabled={!cancelReason.trim()}
                    >
                        Confirm Cancellation
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export { InternalSubscriptionsTable };
